import { Logo } from "@/components/layout/logo";
import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FpSidebarTrigger, useFpSidebar } from "@/components/ui/fp-sidebar";
import { ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useDragAndDrop, useInputFocus, usePersistedPrompts } from "@/hooks";
import { api } from "@/lib/api/api";
import { QUERY_KEYS } from "@/lib/hooks/queryKeys";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useIsProcessing } from "@/lib/hooks/useIsProcessing";
import { useFilesystemStore, useMessageStore } from "@/state/stores";
import { DotsVerticalIcon } from "@radix-ui/react-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { type PropsWithChildren, useEffect, useRef } from "react";
import { ChatContainer } from "./ChatContainer";
import { FilesystemSection } from "./components";

export function Chat({ children }: PropsWithChildren) {
  const isProcessing = useIsProcessing();
  const isLoading = useMessageStore((state) => state.isLoading);
  const messagesLength = useMessageStore((state) => state.messages.length);
  const projectId = useActiveProjectId();
  const queryClient = useQueryClient();
  const router = useRouter();

  const shouldShowFilesystemSection = useFilesystemStore(
    (state) => state.snapshots.length > 0 || state.stagedFiles.length > 0,
  );

  const focusInput = useInputFocus();
  const initialMessageCountRef = useRef(messagesLength);
  const { setOpen } = useFpSidebar();
  const { clearPersistedPrompt } = usePersistedPrompts();

  const archiveProjectMutation = useMutation({
    mutationFn: (projectId: string) => api.archiveProject(projectId),
    onSuccess: () => {
      // Invalidate and refetch project list to remove archived project
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROJECTS] });

      // Clear the draft input message from IndexedDB
      clearPersistedPrompt();

      // Redirect to codegen page
      router.navigate({ to: "/codegen" });
    },
  });

  const handleArchiveProject = () => {
    if (projectId) {
      archiveProjectMutation.mutate(projectId);
    }
  };

  const {
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useDragAndDrop();

  // Focus the textarea when loading and thinking is complete
  useEffect(() => {
    if (!isLoading && !isProcessing) {
      focusInput();
    }
  }, [isLoading, isProcessing, focusInput]);

  // Collapse sidebar when message count increases
  useEffect(() => {
    if (messagesLength > initialMessageCountRef.current) {
      setOpen(false);
      initialMessageCountRef.current = messagesLength;
    }
  }, [messagesLength, setOpen]);

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full">
      <ResizablePanel
        id="chat"
        defaultSize={shouldShowFilesystemSection ? 50 : 100}
        className="h-full flex flex-col bg-background relative"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
      >
        <header className="border-b shrink-0">
          <div className="flex items-center justify-between h-12 px-4 min-h-12">
            <div className="flex items-center gap-2">
              <Logo />
              <FpSidebarTrigger />
            </div>
            {projectId && (
              <FpDropdownMenu>
                <FpDropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <DotsVerticalIcon className="h-4 w-4" />
                  </Button>
                </FpDropdownMenuTrigger>
                <FpDropdownMenuContent align="end">
                  <FpDropdownMenuItem
                    onClick={handleArchiveProject}
                    disabled={archiveProjectMutation.isPending}
                  >
                    {archiveProjectMutation.isPending
                      ? "Archiving..."
                      : "Archive Project"}
                  </FpDropdownMenuItem>
                </FpDropdownMenuContent>
              </FpDropdownMenu>
            )}
          </div>
        </header>

        <ChatContainer>{children}</ChatContainer>
      </ResizablePanel>
      {shouldShowFilesystemSection && <FilesystemSection />}
    </ResizablePanelGroup>
  );
}
